import React from 'react'
import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  Text,
  VStack
} from '@chakra-ui/react'
import { FiCheckCircle } from 'react-icons/fi'
import type { SpotlightPack } from '../../hooks/useSpotlightPacks'

interface PackCardProps {
  pack: SpotlightPack
  isSelected: boolean
  isPopular?: boolean
  onSelect: (packId: string) => void
  disabled?: boolean
}

export const PackCard: React.FC<PackCardProps> = ({
  pack,
  isSelected,
  isPopular = false,
  onSelect,
  disabled = false
}) => {
  const price = (pack.price_cents / 100).toFixed(0)

  return (
    <Card.Root
      borderWidth={isSelected ? '2px' : '1px'}
      borderColor={isSelected ? 'brand.500' : 'gray.200'}
      bg="white"
      borderRadius="2xl"
      shadow={isSelected ? 'xl' : 'sm'}
      position="relative"
      transform={isSelected && isPopular ? { lg: 'scale(1.05)' } : undefined}
      zIndex={isSelected && isPopular ? 10 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      onClick={() => !disabled && onSelect(pack.id)}
      _hover={disabled ? {} : { shadow: 'md', borderColor: 'brand.300' }}
      transition="all 0.2s"
    >
      {isPopular && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bg="brand.500"
          color="white"
          py={2}
          textAlign="center"
          fontWeight="bold"
          fontSize="sm"
          textTransform="uppercase"
          letterSpacing="wide"
          borderTopRadius="2xl"
        >
          Most Popular
        </Box>
      )}
      <Card.Body p={8} pt={isPopular ? 12 : 8}>
        <VStack align="start" gap={6}>
          <Box>
            <Heading size="xl" color="gray.900" mb={2}>
              {pack.impressions.toLocaleString()} Impressions
            </Heading>
            <HStack align="baseline">
              <Heading size="4xl" color={isSelected ? 'brand.600' : 'gray.900'}>
                ${price}
              </Heading>
              <Text color="gray.500">per pack</Text>
            </HStack>
            {pack.description && (
              <Text color="gray.600" mt={3} fontSize="sm">
                {pack.description}
              </Text>
            )}
          </Box>
          <VStack align="start" gap={3} w="full" pt={4} borderTop="1px solid" borderColor="gray.100">
            <HStack gap={2}>
              <Icon as={FiCheckCircle} color="brand.500" />
              <Text fontSize="sm" color="gray.600">
                <strong>{pack.impressions.toLocaleString()} impressions</strong> delivered across your selected platforms
              </Text>
            </HStack>
            <HStack gap={2}>
              <Icon as={FiCheckCircle} color="brand.500" />
              <Text fontSize="sm" color="gray.600">Done-for-you ad creatives</Text>
            </HStack>
            <HStack gap={2}>
              <Icon as={FiCheckCircle} color="brand.500" />
              <Text fontSize="sm" color="gray.600">Simple analytics dashboard</Text>
            </HStack>
            <HStack gap={2}>
              <Icon as={FiCheckCircle} color="brand.500" />
              <Text fontSize="sm" color="gray.600">No subscription required</Text>
            </HStack>
            {pack.recommended_duration_days && (
              <HStack gap={2}>
                <Icon as={FiCheckCircle} color="brand.500" />
                <Text fontSize="sm" color="gray.600">
                  <strong>{pack.recommended_duration_days} days</strong> campaign duration
                </Text>
              </HStack>
            )}
          </VStack>
          <Button
            colorPalette={isSelected ? 'brand' : 'gray'}
            variant={isSelected ? 'solid' : 'outline'}
            size="lg"
            w="full"
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) onSelect(pack.id)
            }}
            isDisabled={disabled}
            fontWeight="bold"
          >
            {isSelected ? 'Selected' : 'Select this pack'}
          </Button>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}



